import React, { useState, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui/modal';
import Button from '@/components/ui/button/Button';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

interface DateRangeFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (dateRange: DateRange) => void;
  initialDateRange: DateRange;
}

export const DateRangeFilterModal: React.FC<DateRangeFilterModalProps> = ({
  isOpen,
  onClose,
  onApply,
  initialDateRange,
}) => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedQuarter, setSelectedQuarter] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>(initialDateRange);

  useLayoutEffect(() => {
    if (isOpen) {
      // Reset selections when modal opens
      setSelectedYear(null);
      setSelectedQuarter(null);
      setSelectedMonth(null);
      setDateRange(initialDateRange);
    }
  }, [isOpen, initialDateRange]);

  const handleYearSelect = (year: number) => {
    setSelectedYear(year);
    setSelectedQuarter(null);
    setSelectedMonth(null);
    setDateRange({
      startDate: new Date(year, 0, 1),
      endDate: new Date(year, 11, 31)
    });
  };

  const handleQuarterSelect = (quarter: number) => {
    if (!selectedYear) return;

    setSelectedQuarter(quarter);
    setSelectedMonth(null);

    const startMonth = (quarter - 1) * 3;
    const endMonth = startMonth + 2;

    setDateRange({
      startDate: new Date(selectedYear, startMonth, 1),
      endDate: new Date(selectedYear, endMonth + 1, 0) // Last day of the end month
    });
  };

  const handleMonthSelect = (month: number) => {
    if (!selectedYear) return;

    setSelectedMonth(month);

    setDateRange({
      startDate: new Date(selectedYear, month, 1),
      endDate: new Date(selectedYear, month + 1, 0) // Last day of the month
    });
  };

  const handleDateRangeChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setDateRange({ startDate: start, endDate: end });

    // Reset period selections if dates are changed manually
    if (start || end) {
      setSelectedYear(null);
      setSelectedQuarter(null);
      setSelectedMonth(null);
    }
  };

  const handleApply = () => {
    onApply(dateRange);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="600px"
    >
      <style>
        {`
          .react-datepicker-wrapper {
            width: 100%;
          }

          .react-datepicker-popper {
            z-index: 20;
          }

          .react-datepicker__month-container {
            float: none !important;
            min-height: 310px !important;
          }

          /* Стили для мобильных устройств */
          @media (max-width: 767px) {
            .react-datepicker-popper {
              position: fixed !important;
              top: auto !important;
              bottom: 0 !important;
              left: 0 !important;
              right: 0 !important;
              transform: none !important;
              margin: 1rem !important;
              width: calc(100% - 2rem) !important;
            }

            .react-datepicker {
              width: 100% !important;
            }

            .react-datepicker__month-container {
              width: 100% !important;
              float: none !important;
            }

            .react-datepicker__day {
              width: calc((100% - 2.8rem) / 7) !important;
              line-height: 2.5rem !important;
              margin: 0.2rem !important;
            }

            .react-datepicker__day-name {
              width: calc((100% - 2.8rem) / 7) !important;
              margin: 0.2rem !important;
            }
          }

          /* Стили для десктопа */
          @media (min-width: 768px) {
            .react-datepicker-wrapper {
              position: relative;
            }

            .react-datepicker-popper {
              z-index: 20;
              margin-top: 0.1rem !important;
            }

            .react-datepicker-popper[data-placement^='bottom-start'] {
              margin-left: 0rem !important;
            }

            .react-datepicker-popper[data-placement^='bottom-end'] {
              margin-right: 0.5rem !important;
            }
          }

          .react-datepicker-popper[data-placement^='bottom'] .react-datepicker__triangle {
            display: none;
          }

          .react-datepicker {
            font-family: inherit;
            border: 1px solid #e5e7eb !important;
            border-radius: 1rem;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
            font-size: 0.875rem;
          }

          .react-datepicker__header {
            background-color: transparent;
            border-bottom: 1px solid #e5e7eb;
            border-radius: 1rem 1rem 0 0;
            padding: 0.75rem 0;
          }

          .react-datepicker__current-month {
            font-weight: 600;
            font-size: 0.875rem;
            color: #1f2937;
            margin-bottom: 0.5rem;
          }

          .react-datepicker__day-name {
            color: #6b7280;
            font-weight: 500;
            font-size: 0.75rem;
            width: 2rem;
            margin: 0.2rem;
          }

          .react-datepicker__day {
            color: #1f2937;
            border-radius: 0.5rem;
            margin: 0.2rem;
            width: 2rem;
            line-height: 2rem;
            font-size: 0.875rem;
          }

          .react-datepicker__day:hover {
            background-color: #3b82f6;
            border-radius: 0.5rem;
          }

          .react-datepicker__day--selected,
          .react-datepicker__day--keyboard-selected {
            background-color: #3b82f6 !important;
            border-radius: 0.5rem;
            color: white !important;
          }

          .react-datepicker__day--disabled {
            color: #9ca3af;
          }

          .react-datepicker__day--in-range {
            background-color: #f3f4f6;
            border-radius: 0.5rem;
          }

          .react-datepicker__day--in-selecting-range {
            background-color: !#f3f4f6;
          }

          .react-datepicker__navigation {
            top: 0.75rem;
          }

          .react-datepicker__navigation-icon::before {
            border-color: #6b7280;
            border-width: 2px 2px 0 0;
            height: 8px;
            width: 8px;
          }

          /* Dark mode styles */
          .dark .react-datepicker {
            background-color: #1e293b;
            border-color: #334155 !important;
          }

          .dark .react-datepicker__header {
            background-color: #1e293b;
            border-color: #334155;
          }

          .dark .react-datepicker__current-month {
            color: #e5e7eb;
          }

          .dark .react-datepicker__day-name {
            color: #9ca3af;
          }

          .dark .react-datepicker__day {
            color: #e5e7eb;
          }

          .dark .react-datepicker__day:hover {
            background-color: #334155;
          }

          .dark .react-datepicker__day--disabled {
            color: #4b5563;
          }

          .dark .react-datepicker__navigation-icon::before {
            border-color: #9ca3af;
          }
        `}
      </style>
      <div className="p-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4 dark:text-white">{t('date_range_filter.title')}</h2>

          {/* Year Selection */}
          <div className="mb-4">
            <div className="text-sm font-medium mb-2 dark:text-white">{t('date_range_filter.year')}</div>
            <div className="flex gap-2">
              {[currentYear, currentYear - 1].map(year => (
                <Button
                  key={year}
                  onClick={() => handleYearSelect(year)}
                  variant={selectedYear === year ? 'primary' : 'outline'}
                >
                  {year}
                </Button>
              ))}
            </div>
          </div>

          {/* Quarter Selection - Always visible */}
          <div className="mb-4">
            <div className="text-sm font-medium mb-2 dark:text-white">{t('date_range_filter.quarter')}</div>
            <div className="min-h-[44px]"> {/* Fixed height container */}
              {selectedYear ? (
                <div className="flex gap-2">
                  {[1, 2, 3, 4].map(quarter => (
                    <Button
                      key={quarter}
                      onClick={() => handleQuarterSelect(quarter)}
                      variant={selectedQuarter === quarter ? 'primary' : 'outline'}
                    >
                      Q{quarter}
                    </Button>
                  ))}
                </div>
              ) : (
                <div className="text-gray-500 text-sm py-2 dark:text-white">{t('date_range_filter.select_year_first')}</div>
              )}
            </div>
          </div>

          {/* Month Selection - Always visible */}
          <div className="mb-4">
            <div className="text-sm font-medium mb-2 dark:text-white">{t('date_range_filter.month')}</div>
            <div className="min-h-[44px]"> {/* Fixed height container */}
              {selectedQuarter ? (
                <div className="flex gap-2">
                  {Array.from({ length: 3 }, (_, i) => {
                    const monthIndex = (selectedQuarter - 1) * 3 + i;
                    const monthName = new Date(2024, monthIndex, 1).toLocaleString('en', { month: 'short' });
                    return (
                      <Button
                        key={monthIndex}
                        onClick={() => handleMonthSelect(monthIndex)}
                        variant={selectedMonth === monthIndex ? 'primary' : 'outline'}
                      >
                        {monthName}
                      </Button>
                    );
                  })}
                </div>
              ) : (
                <div className="text-gray-500 text-sm py-2 dark:text-white">
                  {selectedYear ? t('date_range_filter.select_quarter_first') : t('date_range_filter.select_year_quarter_first')}
                </div>
              )}
            </div>
          </div>

          {/* Date Range Pickers */}
          <div className="grid grid-cols-2 gap-4 mt-6">
            <div>
              <div className="text-sm font-medium mb-2 dark:text-white">{t('date_range_filter.start_date')}</div>
              <ReactDatePicker
                selected={dateRange.startDate}
                onChange={(date) => handleDateRangeChange([date, dateRange.endDate])}
                selectsStart
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                dateFormat="dd.MM.yyyy"
                className="w-full rounded-lg border text-md dark:text-white border-gray-300 bg-transparent py-[9px] px-4 outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-gray-700 dark:bg-form-input dark:focus:border-primary"
              />
            </div>
            <div>
              <div className="text-sm font-medium mb-2 dark:text-white">{t('date_range_filter.end_date')}</div>
              <ReactDatePicker
                selected={dateRange.endDate}
                onChange={(date) => handleDateRangeChange([dateRange.startDate, date])}
                selectsEnd
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                dateFormat="dd.MM.yyyy"
                className="w-full rounded-lg border text-md dark:text-white border-gray-300 bg-transparent py-[9px] px-4 outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-gray-700 dark:bg-form-input dark:focus:border-primary"
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
          >
            {t('common.buttons.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleApply}
          >
            {t('date_range_filter.apply')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
